import { QueryClient } from '@tanstack/react-query';
import { RouteComponentProps } from 'react-router-dom';
import { getCurrentDocumentDataSources, getDocument } from 'services/api/dynamicFormsApi';
import { QUERY_KEYS } from 'services/api/constants';
import { getApiErrorStatus } from 'util/apiErrorUtils';

export const documentQueryKey = (docId: string) => ['DOC', docId];
export const documentDataSourcesQueryKey = QUERY_KEYS.DYNAMIC_FORMS_CURRENT_VERSION_DATA_SOURCES;

// Client errors (403, 404) will not change on a retry; retrying them only delays the redirect.
export const retryUnlessClientError = (failureCount: number, error: Error) => {
  const status = getApiErrorStatus(error);
  if (status !== undefined && status >= 400 && status < 500) {
    return false;
  }

  return failureCount < 3;
};

// Access denied (403) redirects with a toast (as in SW/Compiler); not found (404) - which a Hidden document
// answers too - looks for the EPD's visible copy. Any other error is thrown to the error boundary.
export const isHandledByPage = (error: Error) => {
  const status = getApiErrorStatus(error);
  return status === 403 || status === 404;
};

/**
 * Starts loading the document and its data sources before the page mounts, e.g. while the feature flags
 * are still loading. Neither needs the other, so both start at once.
 */
const prefetchDocument = (queryClient: QueryClient, docId: string) => {
  void queryClient.prefetchQuery({
    queryKey: documentQueryKey(docId),
    queryFn: () => getDocument(docId),
    staleTime: 0,
    retry: retryUnlessClientError,
  });
  void queryClient.prefetchQuery({
    queryKey: documentDataSourcesQueryKey(docId),
    queryFn: () => getCurrentDocumentDataSources(docId),
    staleTime: 0,
    retry: retryUnlessClientError,
  });
};

/** The gate's prefetch for the document page route. */
export const prefetchDynamicForm = (queryClient: QueryClient, { match }: RouteComponentProps) => {
  const { docId } = match.params as { docId?: string };
  if (docId) {
    prefetchDocument(queryClient, docId);
  }
};

/**
 * The page does not reload the document on mount: every visit goes through DynamicFormsGate, whose prefetch
 * (staleTime 0) reloads it, and the page joins that request. On a revisit the cached copy may show until it ends.
 */
export const documentQueryOptions = (docId: string) => ({
  queryKey: documentQueryKey(docId),
  queryFn: () => getDocument(docId),
  throwOnError: (error: Error) => !isHandledByPage(error),
  retry: retryUnlessClientError,
  staleTime: 0,
  refetchOnMount: false,
});

/**
 * The data sources follow the same rule as the document: they depend on data that can change elsewhere
 * (a verifier removed from the company becomes a disabled option), so a cached copy must not survive a revisit.
 * Keyed by the document alone: the server resolves the schema, so this runs alongside the document request.
 * It runs before the document's access check is known, so its 403 and 404 (a Hidden document) fall through to
 * the page's redirects too.
 */
export const documentDataSourcesQueryOptions = (docId: string) => ({
  queryKey: documentDataSourcesQueryKey(docId),
  queryFn: () => getCurrentDocumentDataSources(docId),
  throwOnError: (error: Error) => !isHandledByPage(error),
  retry: retryUnlessClientError,
  staleTime: 0,
  refetchOnMount: false,
});
